import { ArrowUp, Square } from "lucide-react";
import { useTranslate } from "ra-core";

import { MAX_MESSAGE_CHARS } from "./useAskClaude";

// Taster som ikke skal boble videre: cmdk-roten kaller preventDefault på
// Enter/piltaster/Home/End, og dialogens fokusfelle flytter fokus ved Tab.
const CAPTURED_KEYS = new Set([
  "Enter",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
  "Tab",
]);

// Skrivefeltet nederst i chatten (à la Raycast Quick AI). Enter sender,
// Shift+Enter gir linjeskift, Tab blir værende i feltet. Mens et svar
// strømmer, blir send-knappen en stoppknapp. Escape håndteres av dialogen
// (CommandPalette), så den slippes gjennom her.
export const AskInput = ({
  value,
  onChange,
  onSubmit,
  onStop,
  streaming,
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (text: string) => void;
  onStop: () => void;
  // Sant mens et svar strømmer: Enter sender ikke da.
  streaming: boolean;
}) => {
  const translate = useTranslate();
  const canSend = !streaming && value.trim().length > 0;

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // cmdk-roten lytter på Enter/piltaster/Home/End og kaller
    // preventDefault, som ville ødelagt Shift+Enter og markørflytting.
    // Andre taster (Escape, Cmd+K) må boble videre til dialogen og paletten.
    if (CAPTURED_KEYS.has(event.key)) event.stopPropagation();
    if (event.key === "Tab") {
      event.preventDefault();
      return;
    }
    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault();
      if (canSend) onSubmit(value);
    }
  };

  return (
    <div className="shrink-0 px-3 pt-1 pb-3">
      <div
        data-slot="ask-input-wrapper"
        className="flex items-end gap-2 rounded-2xl border border-border/80 bg-background/70 py-1.5 pr-1.5 pl-4 shadow-xs transition-[border-color,box-shadow] focus-within:border-primary/60 focus-within:ring-3 focus-within:ring-primary/15"
      >
        <textarea
          autoFocus
          rows={1}
          maxLength={MAX_MESSAGE_CHARS}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={onKeyDown}
          aria-label={translate("crm.command.ask.label", { _: "Spør Claude" })}
          placeholder={translate("crm.command.ask.placeholder", {
            _: "Spør Claude om noe i Mimin …",
          })}
          className="field-sizing-content max-h-40 min-h-9 flex-1 resize-none bg-transparent py-2 text-sm leading-5 outline-hidden placeholder:text-muted-foreground"
        />
        {streaming ? (
          <button
            type="button"
            onClick={onStop}
            aria-label={translate("crm.command.ask.stop", {
              _: "Stopp svaret",
            })}
            className="grid size-9 shrink-0 place-items-center rounded-xl bg-muted text-foreground transition-colors hover:bg-accent"
          >
            <Square className="size-3.5 fill-current" />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => canSend && onSubmit(value)}
            disabled={!canSend}
            aria-label={translate("crm.command.ask.send", { _: "Send" })}
            className="grid size-9 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground transition-[opacity,background-color] hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground"
          >
            <ArrowUp className="size-4" />
          </button>
        )}
      </div>
    </div>
  );
};
